"use client"

import type { OImageProps } from "#modules/optimization/media/utils"
import type { PropsWithout } from "#types/utils"

import React from "react"
import Image, { getImageProps } from "next/image"

import { twMerge } from "#lib/twMergeConfig"
import { LazyOImage } from "#modules/optimization/media/lazyImage"
import { getPropsForNextImage } from "#modules/optimization/media/utils"

/**
 * An Image that is self standing and grows without additional wrappers etc. inside its parent container to the max dimensions possible, WHILE MAINTAINING THE ASPECT RATIO
 * Reserves space for the image before it is loaded - lazyload compatible
 * Quite common use case
 */
export function SelfGrowingImage(props: PropsWithout<typeof OImage, "fill">) {
  // w-auto wird nur in safari benötigt, damit das bild nicht verzerrt wird - safari kommt mit einem gesetzen aspect-ratio und width:100% nicht klar - in anderen browsern ist es nicht notwendig
  return <OImage {...props} className={twMerge(props.className, "!static !h-auto !w-auto max-w-full")} fill />
}

/**
 * Mostly used
 * Optimized version of the next/image component - makes sure that correct aspect ratio is maintained
 * Can be filled from a media object - also conditionally uses blurDataURL for placeholder when placeholder is set to "blur"
 * Either use media or give an explicit src, alt, width etc.. -> cannot give both
 */
export function OImage(props: OImageProps) {
  const { lazy, ...withoutLazyProps } = props
  if (lazy) {
    return <LazyOImage {...withoutLazyProps} />
  }

  // TODO maybe find out a way (maybe with getImgProps) to not use aspectRatio directly, but to add width and height attributes to all <img> tags so that the browser automatically adds aspectRatio - also better for compatibility and Lighthouse score - at the moment not possible to add them to next/image when also using fill=true - in general these attribs are extremely important for the image to reserve space correctly when image is not loaded yet

  const { nextImageProps, nonMediaImageProps } = getPropsForNextImage(withoutLazyProps)

  return (
    // eslint-disable-next-line jsx-a11y/alt-text
    <Image
      draggable={false}
      {...nextImageProps}
      style={nonMediaImageProps.height && nonMediaImageProps.width ? { ...props.style, aspectRatio: `${nonMediaImageProps.width} / ${nonMediaImageProps.height}` } : props.style}
    />
  )
}

type VaryingImageProps = PropsWithout<typeof Image, "src" | "width" | "height"> & {
  images: {
    src: string

    // Condition for showing the image - media query - not necessary for last image (initial image) - HAS TO BE THE LAST IN THE ARRAY
    mediaCondition: string | undefined
  }[]
}

export function VaryingImage({ images, ...restImageProps }: VaryingImageProps) {
  if (images.length === 0) throw Error("No images provided for VaryingImage - needs at least one image, but in real use cases always at least 2")

  const imageProps = images.map(image => ({ image: getImageProps({ ...restImageProps, src: image.src }), mediaCondition: image.mediaCondition }))

  const initialImage = imageProps.pop()

  return (
    <picture style={initialImage?.image.props.style} className={initialImage?.image.props.className}>
      {imageProps.map(({ mediaCondition, image }, index) => (
        <source key={index} srcSet={image.props.srcSet} media={mediaCondition} />
      ))}
      {/* eslint-disable-next-line jsx-a11y/alt-text */}
      <img {...initialImage!.image.props} />
    </picture>
  )
}
